import { makeAutoObservable, runInAction } from "mobx"
import { createContext } from "react"

import {
    KnowledgeHubAdminService,
    knowledgebase_ListKnowledgeItem,
    MessageAdminService,
    InfoCenterAdminService,
} from "src/api"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { loads } from "src/channel/utils"
import { createLoadingKeys } from "src/lib/loading"
import { EKnowledgeBaseCardSection } from "src/views/knowledge-base/pages/home/types"
import { reportUnhandledApiError } from "src/lib/report"
import { Channel } from "src/channel"
import { Repository } from "src/types/channel"

interface IKnowledgeBaseCard {
    id: string
    cardType: EKnowledgeBaseCardSection
    totalItems: number
}

export class KnowledgeBaseHomeStore implements IDisposable {
    //#region initialization
    static Context = createContext<KnowledgeBaseHomeStore | null>(null)
    static LoadingKeys = createLoadingKeys("loading", "init")

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    _segments: number[] = []
    knowledgeBaseItems: knowledgebase_ListKnowledgeItem[] = []
    cards: IKnowledgeBaseCard[] = []
    error: string | null = null
    private repositoryUpdatesListenerDisposer?: () => void

    constructor() {
        makeAutoObservable(this)
    }
    //#endregion

    //#region getters
    get segments() {
        return this._segments
    }

    private get accessGroupId() {
        return this._accessGroupId
    }
    //#endregion

    //#region setters
    private setSegments(segments: number[]) {
        this._segments = segments
    }

    private setAccessGroup(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private setKnowledgeBaseItems(items: knowledgebase_ListKnowledgeItem[]) {
        // First clear the array then push new items to ensure it's properly observed
        this.knowledgeBaseItems.length = 0
        items.forEach((item) => this.knowledgeBaseItems.push(item))
    }

    private setCards(cards: IKnowledgeBaseCard[]) {
        this.cards.length = 0
        cards.forEach((card) => this.cards.push(card))
    }

    private setError(error: string | null) {
        this.error = error
    }
    //#endregion

    @loads(() => KnowledgeBaseHomeStore.LoadingKeys.init)
    async init(accessGroupId: number = DEFAULT_ACCESS_GROUP.id) {
        try {
            this.setAccessGroup(accessGroupId)
            await this.fetchAllCardCounts()
            this.listenToRepositoryUpdates()
        } catch (error) {
            reportUnhandledApiError(error)
            this.setError("Failed to load knowledge base items")
        }
    }

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }

    private listenToRepositoryUpdates() {
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    (event.payload.repository ===
                        ("knowledge-base" as Repository) ||
                        event.payload.repository === "messages" ||
                        event.payload.repository ===
                            "library-information-items")
                ) {
                    await this.fetchAllCardCounts()
                }
            },
        )
    }

    @loads(() => KnowledgeBaseHomeStore.LoadingKeys.loading)
    async fetchAllCardCounts() {
        try {
            // Fetch all counts in parallel
            const [documentsResponse, noticeBoardResponse, libraryResponse] =
                await Promise.all([
                    this.fetchKnowledgeBaseItems(),
                    this.fetchNoticeBoardCount(),
                    this.fetchLibraryCount(),
                ])

            runInAction(() => {
                this.generateCards(
                    documentsResponse.length,
                    noticeBoardResponse,
                    libraryResponse,
                )
            })
        } catch (error) {
            reportUnhandledApiError(error)
            this.setError("Failed to fetch card counts")
            throw error
        }
    }

    private async fetchKnowledgeBaseItems() {
        const response =
            await KnowledgeHubAdminService.postV1AdminKnowledgebaseKnowledgeItemsList(
                {
                    request: {
                        access_group_id:
                            this.accessGroupId !== DEFAULT_ACCESS_GROUP.id
                                ? this.accessGroupId
                                : undefined,
                        segment_ids:
                            this.segments.length > 0
                                ? this.segments
                                : undefined,
                        page_number: 1,
                        page_size: 50,
                    },
                },
            )

        const items = response.knowledge_items ?? []
        this.setKnowledgeBaseItems(items)
        return items
    }

    private async fetchNoticeBoardCount() {
        const response = await MessageAdminService.noticeBoardGetMessageList({
            request: {
                segment_ids:
                    this.segments.length > 0 ? this.segments : undefined,
            },
            pageNumber: 1,
            pageSize: 1, // We only need the count
            accessGroupId:
                this.accessGroupId !== DEFAULT_ACCESS_GROUP.id
                    ? this.accessGroupId
                    : undefined,
        })

        return response.count ?? 0
    }

    private async fetchLibraryCount() {
        const response = await InfoCenterAdminService.infocenterGetItemListPost(
            {
                request: {
                    page_number: 1,
                    page_size: 1, // We only need the count
                    access_group_id:
                        this.accessGroupId !== DEFAULT_ACCESS_GROUP.id
                            ? this.accessGroupId
                            : undefined,
                    segment_ids:
                        this.segments.length > 0 ? this.segments : undefined,
                },
            },
        )

        return response.total_count ?? 0
    }

    async loadSegments(segments: number[]) {
        this.setSegments(segments)
        try {
            await this.fetchAllCardCounts()
        } catch (error) {
            reportUnhandledApiError(error)
        }
    }

    private generateCards(
        documentsCount: number,
        noticeBoardCount: number,
        libraryCount: number,
    ) {
        const cards: IKnowledgeBaseCard[] = []

        // Only add Documents card if there are knowledge base items
        if (documentsCount > 0) {
            cards.push({
                id: "documents",
                cardType: EKnowledgeBaseCardSection.Documents,
                totalItems: documentsCount,
            })
        }

        // Always add Notice board and Library cards with real counts
        cards.push(
            {
                id: "notice-board",
                cardType: EKnowledgeBaseCardSection.NoticeBoard,
                totalItems: noticeBoardCount,
            },
            {
                id: "library",
                cardType: EKnowledgeBaseCardSection.Library,
                totalItems: libraryCount,
            },
        )

        this.setCards(cards)
    }
}
